import { z } from "zod"

import {
  CommunicationStudioCssVariables,
  CSS_VARIABLE_TYPE_VALUE,
} from "@tfg/types/constants/communication-studio.constant"
import { ZodFormObjectTypeDescription } from "@tfg/types/constants/zodFromInputTypes"
import {
  CSSColorSchema,
  cssLengthSchema,
  fontWeightSchema,
  TextTransformSchema,
  cssTextDecorationSchema,
  alignSchema,
} from "@tfg/types/schemas/communication-studio/cssStylesSchema"

export const CSSVariable = z
  .object({
    name: z.string(),
    type: z.literal(CSS_VARIABLE_TYPE_VALUE).readonly(),
    value: z.string(),
  })
  .describe(ZodFormObjectTypeDescription.ZOD_CSS_VARIABLE)

export type CSSVariablesType = z.infer<typeof CSSVariable>

export const createCSSVariableInstance = <
  N extends string,
  V extends z.ZodTypeAny,
>(
  name: N,
  valueSchema: V
) =>
  CSSVariable.extend({
    name: z.literal(name),
    value: valueSchema.nullable(),
  })

// cssVariables
export const titleColorCSSVariable = createCSSVariableInstance(
  CommunicationStudioCssVariables.TITLE_COLOR,
  CSSColorSchema
).describe(ZodFormObjectTypeDescription.ZOD_CSS_VARIABLE)
export const textColorCssVariable = createCSSVariableInstance(
  CommunicationStudioCssVariables.TEXT_COLOR,
  CSSColorSchema
).describe(ZodFormObjectTypeDescription.ZOD_CSS_VARIABLE)

export const textFontWeightCSSVariable = createCSSVariableInstance(
  CommunicationStudioCssVariables.TEXT_FONT_WEIGHT,
  fontWeightSchema
).describe(ZodFormObjectTypeDescription.ZOD_CSS_VARIABLE)

export const paddingInlineCSSVariable = createCSSVariableInstance(
  CommunicationStudioCssVariables.PADDING_INLINE,
  cssLengthSchema
).describe(ZodFormObjectTypeDescription.ZOD_CSS_VARIABLE)

export const textFontSizeCSSVariable = createCSSVariableInstance(
  CommunicationStudioCssVariables.TEXT_FONT_SIZE,
  cssLengthSchema
).describe(ZodFormObjectTypeDescription.ZOD_CSS_VARIABLE)

export const contentVerticalSpacingCSSVariable = createCSSVariableInstance(
  CommunicationStudioCssVariables.CONTENT_VERTICAL_SPACING,
  cssLengthSchema
).describe(ZodFormObjectTypeDescription.ZOD_CSS_VARIABLE)

export const paddingBlockCSSVariable = createCSSVariableInstance(
  CommunicationStudioCssVariables.PADDING_BLOCK,
  cssLengthSchema
).describe(ZodFormObjectTypeDescription.ZOD_CSS_VARIABLE)

export const headerFontSizeCSSVariable = createCSSVariableInstance(
  CommunicationStudioCssVariables.HEADER_FONT_SIZE,
  cssLengthSchema
).describe(ZodFormObjectTypeDescription.ZOD_CSS_VARIABLE)

export const headerFontWeightCSSVariable = createCSSVariableInstance(
  CommunicationStudioCssVariables.HEADER_FONT_WEIGHT,
  fontWeightSchema
).describe(ZodFormObjectTypeDescription.ZOD_CSS_VARIABLE)

export const headerBackgroundColorCSSVariable = createCSSVariableInstance(
  CommunicationStudioCssVariables.HEADER_BACKGROUND_COLOR,
  CSSColorSchema
).describe(ZodFormObjectTypeDescription.ZOD_CSS_VARIABLE)

export const searchEngineTextColorCSSVariable = createCSSVariableInstance(
  CommunicationStudioCssVariables.SEARCH_ENGINE_TEXT_COLOR,
  CSSColorSchema
).describe(ZodFormObjectTypeDescription.ZOD_CSS_VARIABLE)

export const productCardBadgeBackgroundColorCSSVariable =
  createCSSVariableInstance(
    CommunicationStudioCssVariables.PRODUCT_CARD_BADGE_BACKGROUND_COLOR,
    CSSColorSchema
  ).describe(ZodFormObjectTypeDescription.ZOD_CSS_VARIABLE)

export const productCardBadgeTextColorCSSVariable = createCSSVariableInstance(
  CommunicationStudioCssVariables.PRODUCT_CARD_BADGE_TEXT_COLOR,
  CSSColorSchema
).describe(ZodFormObjectTypeDescription.ZOD_CSS_VARIABLE)

export const letterSpacingCSSVariable = createCSSVariableInstance(
  CommunicationStudioCssVariables.LETTER_SPACING,
  cssLengthSchema
).describe(ZodFormObjectTypeDescription.ZOD_CSS_VARIABLE)

export const headerTextDecorationCSSVariable = createCSSVariableInstance(
  CommunicationStudioCssVariables.HEADER_TEXT_DECORATION,
  cssTextDecorationSchema
).describe(ZodFormObjectTypeDescription.ZOD_CSS_VARIABLE)

export const headerTextTransformCSSVariable = createCSSVariableInstance(
  CommunicationStudioCssVariables.HEADER_TEXT_TRANSFORM,
  TextTransformSchema
).describe(ZodFormObjectTypeDescription.ZOD_CSS_VARIABLE)

export const headerLineHeightCSSVariable = createCSSVariableInstance(
  CommunicationStudioCssVariables.HEADER_LINE_HEIGHT,
  cssLengthSchema
).describe(ZodFormObjectTypeDescription.ZOD_CSS_VARIABLE)

export const headerFontFamilyCSSVariable = createCSSVariableInstance(
  CommunicationStudioCssVariables.HEADER_FONT_FAMILY,
  z.string().nullable()
).describe(ZodFormObjectTypeDescription.ZOD_CSS_VARIABLE)

export const ecoscoreAlignCSSVariable = createCSSVariableInstance(
  CommunicationStudioCssVariables.ECOSCORE_ALIGN,
  alignSchema
).describe(ZodFormObjectTypeDescription.ZOD_CSS_VARIABLE)

export const logoAlignCSSVariable = createCSSVariableInstance(
  CommunicationStudioCssVariables.LOGO_ALIGN,
  alignSchema
).describe(ZodFormObjectTypeDescription.ZOD_CSS_VARIABLE)

export const productAttributesSpacingCSSVariable = createCSSVariableInstance(
  CommunicationStudioCssVariables.PRODUCT_ATTRIBUTES_SPACING,
  cssLengthSchema
).describe(ZodFormObjectTypeDescription.ZOD_CSS_VARIABLE)
